import { useCallback, useEffect, useRef, useState } from 'react';
import { modpackProvisioningApi as api } from '../services/modpackProvisioningApi';
import type { ProvisioningJob, ProvisioningRecoveryAction, ProvisioningTestConfirmation } from '../types/modpackProvisioning';
import type { UpdaterAnswer, UpdaterEvent } from '../types/modpackUpdater';
import { mergeUpdaterEvents, validUpdaterAnswer } from '../utils/modpackUpdater';
import { provisioningCanAnswerRequest } from '../utils/provisioningScheduleRecovery';
import { provisioningCanCancel, provisioningDecisionReady, provisioningFinished } from '../utils/modpackProvisioning';
import { provisioningRemovalRequested, provisioningTestConfirmationCurrent } from '../utils/provisioningTestRemoval';

const message = (error: unknown) => error instanceof Error ? error.message : 'Provisioning could not be reached.';
export type ProvisioningOperation = 'approve' | 'reject' | 'accept' | 'cancel' | ProvisioningRecoveryAction;

/** A browser observes the durable job; closing or changing the tab does not own execution. */
export function useProvisioningJob(selected: string | null, canWrite: boolean, canApprove: boolean) {
  const [job, setJob] = useState<ProvisioningJob | null>(null);
  const [events, setEvents] = useState<UpdaterEvent[]>([]);
  const [pollError, setPollError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const [reload, setReload] = useState(0);
  const writePending = useRef(false);
  const generation = useRef(0);
  const writeRevision = useRef(0);
  const eventCursor = useRef<{ id: string; sequence: number } | null>(null);
  const refresh = useCallback(() => setReload(value => value + 1), []);
  useEffect(() => { const timer = setInterval(() => setClock(Date.now()), 3000); return () => clearInterval(timer); }, []);
  useEffect(() => { setJob(null); setEvents([]); setLastSync(null); setMutationError(null); }, [selected]);
  useEffect(() => {
    generation.current += 1;
    if (!selected) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let cursor = eventCursor.current?.id === selected ? eventCursor.current.sequence : 0;
    let failures = 0;
    const poll = async () => {
      let delay = 1800;
      try {
        const revision = writeRevision.current;
        const [current, history] = await Promise.all([api.job(selected, controller.signal), api.events(selected, cursor, controller.signal)]);
        if (controller.signal.aborted) return;
        if (revision === writeRevision.current && !writePending.current) setJob(current.job);
        setEvents(previous => mergeUpdaterEvents(previous, history.events));
        for (const event of history.events) cursor = Math.max(cursor, event.sequence);
        eventCursor.current = { id: selected, sequence: cursor };
        if (history.events.length && cursor < Math.max(current.job.last_sequence, history.last_sequence ?? 0)) delay = 50;
        else if (provisioningFinished(current.job.state)) delay = 6000;
        failures = 0; setPollError(null); setLastSync(Date.now());
      } catch (error) {
        if (!controller.signal.aborted) { setPollError(message(error)); delay = Math.min(15000, 1800 * 2 ** ++failures); }
      } finally { if (!controller.signal.aborted) timer = setTimeout(poll, delay); }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [selected, reload]);
  const fresh = !!job && job.id === selected && !!lastSync && clock - lastSync < 20000 && !pollError;

  const mutate = async (operation: () => Promise<{ job: ProvisioningJob }>) => {
    if (writePending.current || !fresh) throw new Error('Wait for current progress before continuing.');
    const ownGeneration = generation.current;
    writePending.current = true; writeRevision.current += 1; setBusy(true); setMutationError(null);
    try {
      const result = await operation();
      if (ownGeneration === generation.current) setJob(result.job);
    } catch (error) {
      if (ownGeneration === generation.current) { setMutationError(message(error)); setLastSync(null); }
      throw error;
    } finally {
      writePending.current = false; setBusy(false);
      if (ownGeneration === generation.current) refresh();
    }
  };

  const control = async (operation: ProvisioningOperation, digest?: string, note?: string) => {
    if (!job || !fresh) throw new Error('Wait for current progress before continuing.');
    if (operation === 'approve' || operation === 'reject') {
      if (!canApprove || job.state !== 'awaiting_approval' || !digest || digest !== job.plan_digest) throw new Error('The plan changed. Review it again.');
    } else if (provisioningRemovalRequested(job)) {
      if (!canApprove || operation !== 'retry') throw new Error('A Staff Manager can retry test removal.');
    } else if (!canWrite) throw new Error('Pack Manager access required.');
    if (operation === 'accept' && (job.state !== 'verified' || !digest || digest !== job.plan_digest)) throw new Error('Wait for the verified installation and review it again.');
    if (['approve', 'accept'].includes(operation) && !provisioningDecisionReady(job, events)) throw new Error('Wait for the complete plan and installation checks.');
    if (operation === 'cancel' && !provisioningCanCancel(job)) throw new Error('This request can no longer be cancelled.');
    if (['retry', 'verify', 'cleanup'].includes(operation) && !job.recovery_actions?.includes(operation as ProvisioningRecoveryAction)) throw new Error('This recovery action is no longer available.');
    await mutate(() => operation === 'approve' ? api.approve(job.id, digest!, note)
      : operation === 'reject' ? api.reject(job.id, note ?? '')
        : operation === 'accept' ? api.accept(job.id, digest!)
          : operation === 'cancel' ? api.cancel(job.id) : api.recover(job.id, operation as ProvisioningRecoveryAction));
  };

  const testControl = async (operation: 'designate-test' | 'remove-test', confirmation: ProvisioningTestConfirmation) => {
    if (!job || !fresh || !canApprove) throw new Error('Current progress and Staff Manager access are required.');
    if (!provisioningTestConfirmationCurrent(job, confirmation, operation)) throw new Error('The test or its instances changed. Review them again.');
    await mutate(() => operation === 'designate-test' ? api.designateTest(job.id, confirmation.digest, confirmation.targets)
      : api.removeTest(job.id, confirmation.digest, confirmation.targets));
  };

  const pending = (requestId: string) => {
    const request = job && [job.pending_request, ...job.servers.map(server => server.pending_request)].find(request => request?.id === requestId);
    if (!job || !request || !fresh || provisioningFinished(job.state) || job.cancel_requested || provisioningRemovalRequested(job)
      || !provisioningCanAnswerRequest(request, canWrite, canApprove)) throw new Error('Current progress and permission to answer this request are required.');
    return { job, request };
  };
  const answer = async (requestId: string, value: UpdaterAnswer) => {
    const current = pending(requestId);
    if (current.request.kind === 'schedule' && !validUpdaterAnswer(current.request, value)) throw new Error('Choose a current schedule option before continuing.');
    await mutate(async () => {
      const result = await api.answer(current.job.id, requestId, value);
      if (result.accepted !== true) throw new Error('The answer was not acknowledged. Refresh before continuing.');
      return result;
    });
  };
  const uploadFile = async (requestId: string, file: File) => {
    const current = pending(requestId);
    await mutate(async () => {
      const result = await api.uploadFile(current.job.id, requestId, file);
      if (result.accepted !== true) throw new Error('The upload was not acknowledged. Refresh before continuing.');
      return result;
    });
  };
  return { job, events, pollError, mutationError, lastSync, fresh, busy, refresh, control, testControl, answer, uploadFile };
}
